import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, AlertCircle, CheckCircle2, ExternalLink, Wallet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;

const maskCPF = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const maskPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{0,2})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) => [a && `(${a})`, b && ` ${b}`, c && `-${c}`].filter(Boolean).join(''));
  return d.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
};

const validCPF = (cpf) => {
  const c = (cpf || '').replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(c[i]) * (10 - i);
  let r = (s * 10) % 11; if (r === 10) r = 0;
  if (r !== parseInt(c[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(c[i]) * (11 - i);
  r = (s * 10) % 11; if (r === 10) r = 0;
  return r === parseInt(c[10]);
};

export default function MercadoPagoCheckoutModal({ isOpen, onClose, cart, total, onSuccess }) {
  const [step, setStep] = useState('form'); // form | polling | done | error
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [statusInfo, setStatusInfo] = useState(null);
  const pollIntervalRef = useRef(null);

  const [form, setForm] = useState({ firstName: '', lastName: '', cpf: '', email: '', phone: '' });
  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) {
      setStep('form'); setError(null); setCheckoutUrl(null); setOrderRef(null);
      setStatusInfo(null);
    }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [isOpen]);

  useEffect(() => {
    if (step !== 'polling' || !orderRef) return;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/payments/mercadopago/status/${orderRef}`);
        if (!r.ok) return;
        const data = await r.json();
        setStatusInfo(data);
        if (data.payment_status === 'approved') {
          clearInterval(pollIntervalRef.current);
          setStep('done');
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', { currency: 'BRL', value: data.total || 0 });
          }
          setTimeout(() => { onSuccess?.(); }, 2500);
        }
        if (data.payment_status === 'rejected') {
          clearInterval(pollIntervalRef.current);
          setError('Pagamento recusado ou cancelado. Tente novamente com outro método.');
          setStep('error');
        }
      } catch { /* silent */ }
    };
    pollIntervalRef.current = setInterval(poll, 5000);
    poll();
    return () => clearInterval(pollIntervalRef.current);
  }, [step, orderRef, onSuccess]);

  if (!isOpen) return null;

  const submit = async () => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim()) return setError('Nome e sobrenome obrigatórios.');
    if (!validCPF(form.cpf)) return setError('CPF inválido.');
    if (!/.+@.+\..+/.test(form.email)) return setError('E-mail inválido.');

    setLoading(true);
    try {
      const payload = {
        shopper: {
          first_name: form.firstName.trim(),
          last_name:  form.lastName.trim(),
          cpf:        form.cpf.replace(/\D/g, ''),
          email:      form.email.trim().toLowerCase(),
          phone:      form.phone || null,
        },
        items: cart.map((c, i) => ({
          id:          String(c.id || `item-${i}`),
          description: c.name || c.description || 'Item',
          value:       Number(c.price || c.value || 0),
          quantity:    c.quantity || 1,
        })),
        origin_url: window.location.origin,
      };

      const r = await fetch(`${API}/api/payments/mercadopago/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erro ao criar pagamento');
      setCheckoutUrl(data.checkout_url);
      setOrderRef(data.order_reference);
      setStep('polling');
      window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
      setStep('form');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="mercadopago-modal">
      <div className="bg-slate-900 border border-sky-500/30 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header — azul Mercado Pago */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-800"
             style={{ background: 'linear-gradient(135deg, #009EE3 0%, #0a5f9e 100%)' }}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">Mercado Pago</p>
              <p className="text-sky-100 text-[11px]">PIX · Cartão · Boleto</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="mercadopago-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {/* Formulário */}
          {step === 'form' && (
            <>
              <p className="text-slate-300 text-sm mb-4">
                Pague com PIX, cartão ou boleto. Total: <strong className="text-white">{fmt(total)}</strong>
              </p>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text" placeholder="Nome"
                    value={form.firstName}
                    onChange={e => setField('firstName', e.target.value)}
                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-sky-500"
                    data-testid="mercadopago-firstName"
                  />
                  <input
                    type="text" placeholder="Sobrenome"
                    value={form.lastName}
                    onChange={e => setField('lastName', e.target.value)}
                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-sky-500"
                    data-testid="mercadopago-lastName"
                  />
                </div>
                <input
                  type="text" placeholder="CPF *" inputMode="numeric"
                  value={form.cpf}
                  onChange={e => setField('cpf', maskCPF(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-sky-500"
                  data-testid="mercadopago-cpf"
                />
                <input
                  type="email" placeholder="E-mail *"
                  value={form.email}
                  onChange={e => setField('email', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-sky-500"
                  data-testid="mercadopago-email"
                />
                <input
                  type="tel" placeholder="WhatsApp (opcional)" inputMode="tel"
                  value={form.phone}
                  onChange={e => setField('phone', maskPhone(e.target.value))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-sky-500"
                  data-testid="mercadopago-phone"
                />
              </div>

              {error && (
                <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-xs flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{error}
                </div>
              )}

              <button
                onClick={submit}
                disabled={loading}
                className="mt-4 w-full py-3 text-white font-bold rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                style={{ background: '#009EE3' }}
                onMouseEnter={e => e.currentTarget.style.background = '#00b1ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#009EE3'}
                data-testid="mercadopago-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                Pagar {fmt(total)} com Mercado Pago
              </button>

              <p className="text-[10px] text-slate-500 text-center mt-3">
                Você será redirecionado ao checkout seguro do Mercado Pago.
              </p>
            </>
          )}

          {/* Aguardando pagamento */}
          {step === 'polling' && (
            <div className="py-6 text-center">
              <Loader2 className="w-10 h-10 text-sky-400 animate-spin mx-auto mb-3" />
              <p className="text-white font-semibold mb-1">Aguardando confirmação...</p>
              <p className="text-slate-400 text-xs mb-4">Conclua o pagamento na aba do Mercado Pago</p>
              {checkoutUrl && (
                <a
                  href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-600/20 border border-sky-500/40 rounded-full text-sky-300 text-xs hover:bg-sky-600/30"
                  data-testid="mercadopago-reopen"
                >
                  <ExternalLink className="w-3 h-3" /> Reabrir Mercado Pago
                </a>
              )}
              {statusInfo && statusInfo.mp_status && (
                <p className="text-[10px] text-slate-500 mt-3">Status: {statusInfo.mp_status}</p>
              )}
              <p className="text-[10px] text-slate-500 mt-2">PIX e cartão confirmam na hora · Boleto pode levar até 2 dias úteis</p>
            </div>
          )}

          {/* Sucesso */}
          {step === 'done' && (
            <div className="py-6 text-center" data-testid="mercadopago-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-bold mb-1">Pagamento confirmado!</p>
              <p className="text-slate-400 text-xs">Em instantes você receberá o e-mail com a licença.</p>
            </div>
          )}

          {/* Erro */}
          {step === 'error' && (
            <div className="py-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-white font-bold mb-1">Ops!</p>
              <p className="text-slate-400 text-xs mb-4">{error}</p>
              <button onClick={() => setStep('form')} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg" data-testid="mercadopago-retry">
                Tentar novamente
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
